import './App.css';
import Router from './Router';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getNotificationOriginName, getSessionStorage, getStorage, isVesselCrew, playNotificationSound, sessionStorageUpdateEventName, setSessionStorage, setStorage } from './utils';
import constant from './constant';
import { MessageProvider, useMessageContext } from './context/MessageContext'; // 👈
import { v1_fetch_globalSetting } from './actions/v1/globalSettings';
import { GlobalDataProvider, useGlobalData } from './context/GlobalDataContext';
import { NotificationProvider, useNotificationContext } from './context/NotificationContext';
import { v4_chat_incoming, v4_mailbox_configuration, v4_mailbox_emails } from './actions/v4/mailBox';
import FloatingChatDock from './components/MailBox/floatingChatDock';
import AIAssistantButton from './components/AI/AIAssistantButton';

const canUseBrowserNotifications = () => typeof window !== 'undefined' && 'Notification' in window;
const getCurrentUserId = () => {
  try {
    const encoded = getSessionStorage(constant.keys.userDetails);
    if (encoded) {
      const user = JSON.parse(atob(encoded));
      const id = Number(user?.id);
      if (Number.isFinite(id)) {
        return id;
      }
    }
  } catch (error) {
    // Fall through to token decoding.
  }

  try {
    const token = String(getSessionStorage(constant.keys.userToken) || '').trim();
    if (!token) return null;

    const tokenParts = token.split('.');
    if (tokenParts.length < 2) return null;

    const base64Payload = tokenParts[1].replace(/-/g, '+').replace(/_/g, '/');
    const normalizedPayload = base64Payload.padEnd(base64Payload.length + (4 - (base64Payload.length % 4 || 4)) % 4, '=');
    const payload = JSON.parse(atob(normalizedPayload));
    const id = Number(payload?.USER_ID ?? payload?.userId ?? payload?.id ?? payload?.sub);
    return Number.isFinite(id) ? id : null;
  } catch (error) {
    return null;
  }
};

const getMailboxSnapshotStorageKey = () => `${constant.keys.mailboxUnreadSnapshot}.${getCurrentUserId() || 'guest'}`;
const getChatNotificationCursorStorageKey = () => `${constant.keys.chatNotificationCursor}.${getCurrentUserId() || 'guest'}`;
const toTrimmedText = (value) => String(value ?? '').trim();

const getNetworkAwarePollingIntervalMs = (channel = 'chat') => {
  const defaultInterval = channel === 'mailbox' ? 120000 : 90000;

  if (typeof navigator === 'undefined') {
    return defaultInterval;
  }

  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const effectiveType = String(connection?.effectiveType || '').toLowerCase();
  const saveDataEnabled = Boolean(connection?.saveData);

  if (saveDataEnabled || effectiveType.includes('2g')) {
    return channel === 'mailbox' ? 300000 : 240000;
  }

  if (effectiveType === '3g') {
    return channel === 'mailbox' ? 240000 : 180000;
  }

  return defaultInterval;
};

const coerceChatUserId = (...candidates) => {
  for (const candidate of candidates) {
    const parsed = Number(candidate);
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed;
    }
  }
  return null;
};

const coercePositiveInteger = (...candidates) => {
  for (const candidate of candidates) {
    const parsed = Number(candidate);
    if (Number.isInteger(parsed) && parsed > 0) {
      return parsed;
    }
  }
  return null;
};

const buildAttachmentPreviewLabel = (fileName, fileUrl) => {
  const normalizedName = toTrimmedText(fileName);
  if (normalizedName) {
    return `Attachment: ${normalizedName}`;
  }

  if (toTrimmedText(fileUrl)) {
    return 'Attachment';
  }

  return '';
};

const normalizeIncomingChatEvent = (payload = {}) => {
  const rawMessageData = payload?.messageData && typeof payload.messageData === 'object'
    ? payload.messageData
    : {};

  const senderId = coerceChatUserId(
    payload?.from,
    payload?.senderId,
    rawMessageData?.from_user_id,
    rawMessageData?.fromUserId,
    rawMessageData?.from_user,
  );
  const receiverId = coerceChatUserId(
    payload?.to,
    payload?.receiverId,
    rawMessageData?.to_user_id,
    rawMessageData?.toUserId,
    rawMessageData?.to_user,
  );
  const messageId = coercePositiveInteger(rawMessageData?.id, payload?.messageId, payload?.id);

  const messageText = String(
    rawMessageData?.text
      ?? payload?.messageText
      ?? payload?.message
      ?? '',
  );
  const fileName = toTrimmedText(
    rawMessageData?.file_name
      || rawMessageData?.fileName
      || payload?.fileName
      || payload?.originalFileName
      || '',
  );
  const fileUrl = toTrimmedText(rawMessageData?.file_url || rawMessageData?.fileUrl || payload?.fileUrl || '');
  const fromName = toTrimmedText(
    payload?.fromName
      || payload?.senderName
      || rawMessageData?.from_user
      || payload?.from_user
      || '',
  );
  const toName = toTrimmedText(payload?.toName || rawMessageData?.to_user || '');
  const explicitPreview = toTrimmedText(payload?.messagePreview || payload?.previewMessage || '');
  const textPreview = toTrimmedText(messageText);
  const attachmentPreview = buildAttachmentPreviewLabel(fileName, fileUrl);
  const messagePreview = explicitPreview || textPreview || attachmentPreview || 'New message received';

  return {
    type: 'new_message_notification',
    ...payload,
    from: senderId ?? payload?.from ?? rawMessageData?.from_user_id ?? payload?.senderId,
    to: receiverId ?? payload?.to ?? rawMessageData?.to_user_id ?? payload?.receiverId,
    fromName: fromName || payload?.fromName || '',
    messagePreview,
    timestamp: payload?.timestamp || rawMessageData?.send_on || rawMessageData?.sendOn || payload?.sendOn || new Date().toISOString(),
    messageData: {
      ...rawMessageData,
      id: messageId ?? rawMessageData?.id ?? null,
      text: messageText,
      tagged_message: rawMessageData?.tagged_message ?? rawMessageData?.taggedMessage ?? payload?.tagged_message ?? payload?.taggedMessage ?? null,
      send_on: rawMessageData?.send_on || rawMessageData?.sendOn || payload?.sendOn || payload?.timestamp || new Date().toISOString(),
      from_user: rawMessageData?.from_user || fromName || '',
      to_user: rawMessageData?.to_user || toName || '',
      type: rawMessageData?.type || payload?.messageType || 'received',
      file_name: fileName || rawMessageData?.file_name || '',
      file_url: fileUrl || rawMessageData?.file_url || '',
      from_user_id: senderId ?? rawMessageData?.from_user_id ?? null,
      to_user_id: receiverId ?? rawMessageData?.to_user_id ?? null,
      has_attachment: rawMessageData?.has_attachment ?? Boolean(fileName || fileUrl),
    },
  };
};

function AppWrapper() {

  return (
    <GlobalDataProvider >
      <NotificationProvider>
        <MessageProvider>
          <App />
        </MessageProvider>
      </NotificationProvider>
    </GlobalDataProvider>
  );
}

function App() {
  const ws = useRef(null);
  const mailboxSnapshotRef = useRef({});
  const mailboxPollInFlightRef = useRef(false);
  const chatCatchupInFlightRef = useRef(false);
  const chatNotificationCursorRef = useRef(0);
  const notifyClientRef = useRef(null);
  const pushNewMessageRef = useRef(null);
  const [sessionToken, setSessionToken] = useState(() => getSessionStorage(constant.keys.userToken));
  const canUseChat = !isVesselCrew();
  const [onlineUsers, setOnlineUsers] = useState([]);
  const { pushNewMessage } = useMessageContext(); // 👈 access context updater
  const { pushNotification } = useNotificationContext();
  const { setGlobalData } = useGlobalData();

  const buildMailboxNotificationLink = useCallback((meta = {}) => {
    const params = new URLSearchParams();
    const messageId = String(meta?.messageId || '').trim();
    const mailboxId = String(meta?.mailboxId || '').trim();
    const folder = String(meta?.folder || '').trim();

    if (messageId) {
      params.set('openMessageId', messageId);
    }
    if (mailboxId) {
      params.set('openMailboxId', mailboxId);
    }
    if (folder) {
      params.set('openFolder', folder);
    }

    const query = params.toString();
    return query
      ? `${constant.Authedcomponent.mailBox.url}?${query}`
      : constant.Authedcomponent.mailBox.url;
  }, []);

  const buildChatNotificationLink = useCallback((meta = {}) => {
    const params = new URLSearchParams();
    const chatUserId = coerceChatUserId(
      meta?.chatUserId,
      meta?.from,
      meta?.senderId,
      meta?.fromUserId,
    );
    const chatUserName = toTrimmedText(
      meta?.chatUserName
        || meta?.fromName
        || meta?.senderName
        || '',
    );

    if (chatUserId) {
      params.set('openChatUserId', String(chatUserId));
    }
    if (chatUserName) {
      params.set('openChatUserName', chatUserName);
    }

    const query = params.toString();
    return query
      ? `${constant.Authedcomponent.mailBox.url}?${query}`
      : constant.Authedcomponent.mailBox.url;
  }, []);

  const notifyClient = useCallback((payload = {}) => {
    const {
      type = 'system',
      title = 'Notification',
      message = '',
      toastVariant = 'info',
      toastText = '',
      showToast = true,
      browserTitle,
      link = '',
      meta = {},
      originName = '',
      dedupeKey = null,
    } = payload;
    const resolvedOriginName = getNotificationOriginName({ originName, meta });
    const normalizedMeta = resolvedOriginName
      ? { ...meta, originName: resolvedOriginName }
      : { ...meta };

    playNotificationSound();
    pushNotification({
      type,
      title,
      message,
      link,
      meta: normalizedMeta,
      dedupeKey,
      createdAt: new Date().toISOString(),
    });

    const textForToast = toastText || message || title;
    if (showToast) {
      if (toastVariant === 'success') {
        toast.success(textForToast);
      } else if (toastVariant === 'warning') {
        toast.warn(textForToast);
      } else if (toastVariant === 'error') {
        toast.error(textForToast);
      } else {
        toast.info(textForToast);
      }
    }

    if (canUseBrowserNotifications() && Notification.permission === 'granted') {
      const browserBody = [
        resolvedOriginName ? `From: ${resolvedOriginName}` : '',
        message || textForToast,
      ].filter(Boolean).join('\n');

      new Notification(browserTitle || title, {
        body: browserBody,
      });
    }
  }, [pushNotification]);

  useEffect(() => {
    notifyClientRef.current = notifyClient;
  }, [notifyClient]);

  useEffect(() => {
    pushNewMessageRef.current = pushNewMessage;
  }, [pushNewMessage]);

  const persistChatCursor = useCallback((nextCursor) => {
    const numericCursor = Number(nextCursor);
    if (!Number.isInteger(numericCursor) || numericCursor <= 0) return;
    if (numericCursor <= chatNotificationCursorRef.current) return;

    chatNotificationCursorRef.current = numericCursor;
    setStorage(getChatNotificationCursorStorageKey(), numericCursor);
  }, []);

  useEffect(() => {
    const syncSessionToken = () => {
      const latestToken = getSessionStorage(constant.keys.userToken);
      setSessionToken((prevToken) => (prevToken === latestToken ? prevToken : latestToken));
    };

    syncSessionToken();

    if (typeof window === 'undefined') return undefined;

    const handleSessionStorageUpdate = (event) => {
      const key = String(event?.detail?.key || '');
      if (!key || key === constant.keys.userToken || key === constant.keys.userDetails) {
        syncSessionToken();
      }
    };

    window.addEventListener(sessionStorageUpdateEventName, handleSessionStorageUpdate);
    window.addEventListener('focus', syncSessionToken);
    document.addEventListener('visibilitychange', syncSessionToken);

    const syncInterval = setInterval(syncSessionToken, 15000);

    return () => {
      window.removeEventListener(sessionStorageUpdateEventName, handleSessionStorageUpdate);
      window.removeEventListener('focus', syncSessionToken);
      document.removeEventListener('visibilitychange', syncSessionToken);
      clearInterval(syncInterval);
    };
  }, []);

  useEffect(() => {
    const storedBase64 = getSessionStorage(constant.keys.globalDetails);

    if (storedBase64) {
      try {
        const jsonString = atob(storedBase64);
        const parsedData = JSON.parse(jsonString);
        document.title = `${parsedData.COMPANY_NAME} | New-building Ship Site Management 2.0`
        setGlobalData(parsedData);
      } catch (error) {
        console.error('Failed to decode base64 globalData:', error);
      }
    } else {
      v1_fetch_globalSetting().then((res) => {
        if (res.status) {
          const base64Encoded = btoa(JSON.stringify(res.result));
          setSessionStorage(constant.keys.globalDetails, base64Encoded);
          setGlobalData(res.result);
          document.title = `${res.result.COMPANY_NAME} | New-building Ship Site Management 2.0`

        }
      });
    }
  }, [setGlobalData]);

  useEffect(() => {
    const apiBaseUrl = process.env.REACT_APP_BASE_URL?.trim().replace(/\/+$/, '');
    const webSocketUrl = process.env.REACT_APP_WEB_SOCKET_URL || apiBaseUrl?.replace(/^http/i, 'ws');

    if (!sessionToken || !webSocketUrl) {
      return undefined;
    }

    let isUnmounted = false;
    let reconnectTimer = null;
    let reconnectAttempt = 0;

    const scheduleReconnect = () => {
      if (isUnmounted || reconnectTimer) return;

      const delay = Math.min(30000, 1000 * (2 ** reconnectAttempt));
      reconnectAttempt += 1;

      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        connect();
      }, delay);
    };

    const connect = () => {
      if (isUnmounted) return;

      const activeSocket = ws.current;
      if (activeSocket && (activeSocket.readyState === WebSocket.OPEN || activeSocket.readyState === WebSocket.CONNECTING)) {
        return;
      }

      try {
        const socket = new WebSocket(webSocketUrl);
        ws.current = socket;

        socket.onopen = () => {
          reconnectAttempt = 0;
          console.log('🔌 WebSocket connected');
          socket.send(JSON.stringify({ type: 'login', token: sessionToken }));
        };

        socket.onerror = (error) => {
          console.error('❌ WebSocket error:', error);
          if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
            socket.close();
          }
        };

        socket.onclose = () => {
          console.log('🔌 WebSocket closed');
          if (ws.current === socket) {
            ws.current = null;
          }
          if (!isUnmounted) {
            scheduleReconnect();
          }
        };

        socket.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            console.log('📨 Message from server:', data);

            if (data.type === 'users') {
              setOnlineUsers(data.data || []);
            }

            if (canUseChat && data.type === 'new_message_notification') {
              const normalizedChatEvent = normalizeIncomingChatEvent(data);
              persistChatCursor(normalizedChatEvent?.messageData?.id);

              const senderId = Number(normalizedChatEvent.from ?? normalizedChatEvent?.messageData?.from_user_id);
              const currentUserId = getCurrentUserId();
              if (Number.isFinite(senderId) && Number.isFinite(currentUserId) && senderId === currentUserId) {
                return;
              }

              pushNewMessageRef.current?.(normalizedChatEvent);
              notifyClientRef.current?.({
                type: 'dm',
                title: 'Personal Message',
                message: normalizedChatEvent.messagePreview || `New message from ${normalizedChatEvent.fromName || 'a teammate'}`,
                toastText: `New message from ${normalizedChatEvent.fromName || 'a teammate'}`,
                toastVariant: 'success',
                browserTitle: '📩 New Message',
                link: buildChatNotificationLink({
                  from: normalizedChatEvent.from,
                  fromName: normalizedChatEvent.fromName || normalizedChatEvent?.messageData?.from_user,
                }),
                originName: normalizedChatEvent.fromName || normalizedChatEvent?.messageData?.from_user || '',
                meta: {
                  from: normalizedChatEvent.from,
                  fromName: normalizedChatEvent.fromName,
                  messageId: normalizedChatEvent?.messageData?.id,
                  hasAttachment: Boolean(normalizedChatEvent?.messageData?.has_attachment),
                },
                dedupeKey: normalizedChatEvent?.messageData?.id ? `dm-${normalizedChatEvent.messageData.id}` : null,
              });
            }

            if (data.type === 'mail_tag_assignment_notification') {
              notifyClientRef.current?.({
                type: 'tag',
                title: 'Email Assigned',
                message: data.messagePreview || `${data.assignedByName || 'A teammate'} assigned an email to you.`,
                toastText: data.messagePreview || 'You were assigned to an email.',
                toastVariant: 'info',
                browserTitle: '🏷️ Email Assignment',
                link: buildMailboxNotificationLink({ messageId: data.messageId }),
                originName: data.assignedByName || '',
                meta: {
                  messageId: data.messageId,
                  assignedBy: data.assignedBy,
                  assignedByName: data.assignedByName,
                },
                dedupeKey: data.messageId ? `tag-assignment-${data.messageId}` : null,
              });
            }

            if (data.type === 'mail_comment_notification') {
              notifyClientRef.current?.({
                type: 'comment',
                title: 'New Email Comment',
                message: data.messagePreview || `${data.commentedByName || 'A teammate'} commented on an email.`,
                toastText: data.messagePreview || 'A new comment was added to an email.',
                toastVariant: 'info',
                browserTitle: '💬 Email Comment',
                link: buildMailboxNotificationLink({ messageId: data.messageId }),
                originName: data.commentedByName || '',
                meta: {
                  messageId: data.messageId,
                  commentId: data.commentId,
                  commentedBy: data.commentedBy,
                  commentedByName: data.commentedByName,
                  emailSubject: data.emailSubject,
                  emailFromAddress: data.emailFromAddress,
                },
                dedupeKey: data.commentId
                  ? `mail-comment-${data.commentId}`
                  : (data.messageId ? `mail-comment-${data.messageId}-${data.timestamp || Date.now()}` : null),
              });
            }

            if (data.type === 'inspection_assignment_notification') {
              notifyClientRef.current?.({
                type: 'inspection',
                title: 'Inspection Assigned',
                message: data.messagePreview || `${data.assignedByName || 'A teammate'} assigned an inspection to you.`,
                toastText: data.messagePreview || 'A new inspection has been assigned to you.',
                toastVariant: 'info',
                browserTitle: '🛠️ Inspection Assignment',
                link: constant.Authedcomponent.inspectionProject.url,
                originName: data.assignedByName || '',
                meta: {
                  inspectionId: data.inspectionId,
                  inspectionName: data.inspectionName,
                  inspectionNumber: data.inspectionNumber,
                  assignedBy: data.assignedBy,
                  assignedByName: data.assignedByName,
                },
                dedupeKey: data.inspectionId ? `inspection-assignment-${data.inspectionId}` : null,
              });
            }

            if (data.type === 'fat_assignment_notification') {
              notifyClientRef.current?.({
                type: 'fat',
                title: 'FAT Assignment',
                message: data.messagePreview || `${data.assignedByName || 'A teammate'} assigned a FAT to you.`,
                toastText: data.messagePreview || 'A FAT has been assigned to you.',
                toastVariant: 'info',
                browserTitle: '📌 FAT Assignment',
                link: constant.Authedcomponent.fatIndex.url,
                originName: data.assignedByName || '',
                meta: {
                  fatId: data.fatId,
                  fatSubject: data.fatSubject,
                  fatNumber: data.fatNumber,
                  roleKeys: data.roleKeys,
                  roleLabels: data.roleLabels,
                  assignedBy: data.assignedBy,
                  assignedByName: data.assignedByName,
                },
                dedupeKey: data.fatId ? `fat-assignment-${data.fatId}-${(data.roleKeys || []).join('-')}` : null,
              });
            }

            if (data.type === 'leave_application_submitted') {
              notifyClientRef.current?.({
                type: 'leave',
                title: 'New Leave Request',
                message: data.messagePreview || `${data.applicantName || 'A teammate'} submitted a leave request.`,
                toastText: data.messagePreview || 'A new leave request needs your review.',
                toastVariant: 'info',
                browserTitle: '🌴 Leave Request',
                link: constant.Authedcomponent.leaveApplications.url,
                originName: data.applicantName || '',
                meta: {
                  leaveId: data.recordId,
                  applicantId: data.applicantId,
                  applicantName: data.applicantName,
                },
                dedupeKey: data.recordId ? `leave-submitted-${data.recordId}` : null,
              });
            }

            if (data.type === 'expense_application_submitted') {
              notifyClientRef.current?.({
                type: 'expense',
                title: 'New Expense Request',
                message: data.messagePreview || `${data.applicantName || 'A teammate'} submitted an expense request.`,
                toastText: data.messagePreview || 'A new expense request needs your review.',
                toastVariant: 'info',
                browserTitle: '🧾 Expense Request',
                link: constant.Authedcomponent.expenseApplications.url,
                originName: data.applicantName || '',
                meta: {
                  expenseId: data.recordId,
                  applicantId: data.applicantId,
                  applicantName: data.applicantName,
                },
                dedupeKey: data.recordId ? `expense-submitted-${data.recordId}` : null,
              });
            }
          } catch (err) {
            console.error('⚠️ Error parsing WebSocket message:', err);
          }
        };
      } catch (error) {
        console.error('❌ WebSocket setup failed:', error);
        scheduleReconnect();
      }
    };

    connect();

    return () => {
      isUnmounted = true;
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
      }
      if (ws.current) {
        ws.current.onopen = null;
        ws.current.onerror = null;
        ws.current.onclose = null;
        ws.current.onmessage = null;
        ws.current.close(1000, 'Component unmounted');
        ws.current = null;
      }
    };
  }, [buildChatNotificationLink, buildMailboxNotificationLink, canUseChat, persistChatCursor, sessionToken]);

  useEffect(() => {
    if (!sessionToken || !canUseChat) return undefined;

    const cursorStoreKey = getChatNotificationCursorStorageKey();
    const storedCursor = Number(getStorage(cursorStoreKey));
    chatNotificationCursorRef.current = Number.isInteger(storedCursor) && storedCursor > 0
      ? storedCursor
      : 0;

    let active = true;

    const buildMessageEventFromCatchup = (item = {}) => {
      const fallbackAttachmentPreview = buildAttachmentPreviewLabel(item.fileName, item.fileUrl);

      return normalizeIncomingChatEvent({
        type: 'new_message_notification',
        from: Number(item.senderId),
        fromName: item.senderName || '',
        messagePreview: toTrimmedText(item.messageText) || fallbackAttachmentPreview || 'New message received',
        timestamp: item.sendOn || new Date().toISOString(),
        messageData: {
          id: Number(item.messageId),
          text: item.messageText || '',
          tagged_message: item.taggedMessage || null,
          send_on: item.sendOn || new Date().toISOString(),
          from_user: item.senderName || '',
          to_user: '',
          type: 'received',
          file_name: item.fileName || '',
          file_url: item.fileUrl || '',
          from_user_id: Number(item.senderId),
          to_user_id: getCurrentUserId(),
          has_attachment: Boolean(toTrimmedText(item.fileName) || toTrimmedText(item.fileUrl)),
        },
      });
    };

    const pushChatCatchupNotifications = (items = []) => {
      const notificationsToShow = items.slice(-3);
      const hiddenCount = Math.max(0, items.length - notificationsToShow.length);

      notificationsToShow.forEach((item) => {
        notifyClientRef.current?.({
          type: 'dm',
          title: 'Personal Message',
          message: item.messagePreview || `New message from ${item.fromName || 'a teammate'}`,
          toastText: `New message from ${item.fromName || 'a teammate'}`,
          toastVariant: 'success',
          browserTitle: '📩 New Message',
          link: buildChatNotificationLink({
            from: item.from,
            fromName: item.fromName,
          }),
          originName: item.fromName || '',
          meta: {
            from: item.from,
            fromName: item.fromName,
            messageId: item?.messageData?.id,
          },
          dedupeKey: item?.messageData?.id ? `dm-${item.messageData.id}` : null,
        });
      });

      if (hiddenCount > 0) {
        notifyClientRef.current?.({
          type: 'dm',
          title: 'More Messages',
          message: `${hiddenCount} more message${hiddenCount > 1 ? 's' : ''} arrived while you were offline.`,
          toastText: `${hiddenCount} more unread chat message${hiddenCount > 1 ? 's' : ''}.`,
          toastVariant: 'info',
          browserTitle: '📩 Unread Messages',
          link: constant.Authedcomponent.mailBox.url,
          dedupeKey: `dm-offline-summary-${items[items.length - 1]?.messageData?.id || Date.now()}`,
        });
      }
    };

    const pollChatCatchup = async () => {
      if (chatCatchupInFlightRef.current) return;
      if (typeof document !== 'undefined' && document.hidden) return;

      chatCatchupInFlightRef.current = true;
      try {
        const currentUserId = getCurrentUserId();
        const afterMessageId = Number(chatNotificationCursorRef.current) > 0
          ? Number(chatNotificationCursorRef.current)
          : 0;
        const catchupRes = await v4_chat_incoming(afterMessageId, 30);
        if (!active || !catchupRes?.status) return;

        const messages = Array.isArray(catchupRes?.result?.messages) ? catchupRes.result.messages : [];
        if (messages.length === 0) return;

        const mappedEvents = messages
          .map((item) => buildMessageEventFromCatchup(item))
          .filter((event) => Number.isInteger(event?.messageData?.id) && event.messageData.id > 0)
          .filter((event) => {
            const senderId = Number(event?.from);
            return !Number.isFinite(currentUserId) || !Number.isFinite(senderId) || senderId !== currentUserId;
          });

        mappedEvents.forEach((eventPayload) => {
          pushNewMessageRef.current?.(eventPayload);
        });

        if (mappedEvents.length > 0) {
          pushChatCatchupNotifications(mappedEvents);
        }

        const serverCursor = Number(catchupRes?.result?.cursor);
        const localHighest = mappedEvents[mappedEvents.length - 1]?.messageData?.id;
        persistChatCursor(Number.isInteger(serverCursor) && serverCursor > 0 ? serverCursor : localHighest);
      } catch (error) {
        console.error('Chat catch-up polling failed:', error);
      } finally {
        chatCatchupInFlightRef.current = false;
      }
    };

    const pollIntervalMs = getNetworkAwarePollingIntervalMs('chat');
    pollChatCatchup();
    const interval = setInterval(pollChatCatchup, pollIntervalMs);

    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [buildChatNotificationLink, canUseChat, persistChatCursor, sessionToken]);

  useEffect(() => {
    if (!sessionToken) return undefined;

    const snapshotStoreKey = getMailboxSnapshotStorageKey();
    mailboxSnapshotRef.current = getStorage(snapshotStoreKey) || {};

    let active = true;

    const pollMailboxForNewUnread = async () => {
      if (mailboxPollInFlightRef.current) {
        return;
      }
      if (typeof document !== 'undefined' && document.hidden) return;

      mailboxPollInFlightRef.current = true;
      try {
        const mailboxRes = await v4_mailbox_configuration();
        if (!active || !mailboxRes?.status) return;

        const mailboxes = Array.isArray(mailboxRes.result) ? mailboxRes.result : [];
        for (const mailbox of mailboxes) {
          if (!active || !mailbox?.ID) continue;

          const mailboxId = String(mailbox.ID);
          const mailsRes = await v4_mailbox_emails(1, 20, 'INBOX', mailbox.ID);
          if (!active || !mailsRes?.status) continue;

          const mails = Array.isArray(mailsRes?.result?.mails) ? mailsRes.result.mails : [];
          const unreadMails = mails.filter((mail) => mail?.read === false && mail?.messageId);
          const unreadIds = unreadMails.map((mail) => String(mail.messageId));

          const existingSnapshot = mailboxSnapshotRef.current[mailboxId];
          if (!Array.isArray(existingSnapshot)) {
            if (unreadMails.length > 0) {
              const detailed = unreadMails.slice(0, 3);
              detailed.forEach((mail) => {
                const sender = mail.fromName || mail.fromAddress || 'Unknown sender';
                const subject = mail.subject || '(No subject)';

                notifyClient({
                  type: 'email',
                  title: 'Unread Email',
                  message: `${sender}: ${subject}`,
                  toastText: `Unread email from ${sender}`,
                  toastVariant: 'info',
                  browserTitle: '📧 Unread Email',
                  link: buildMailboxNotificationLink({
                    messageId: mail.messageId,
                    mailboxId: mailbox.ID,
                    folder: 'INBOX',
                  }),
                  originName: sender,
                  meta: {
                    mailboxId: mailbox.ID,
                    mailboxEmail: mailbox.EMAIL,
                    messageId: mail.messageId,
                    sender,
                    subject,
                  },
                  dedupeKey: `email-${mailboxId}-${mail.messageId}`,
                });
              });

              if (unreadMails.length > detailed.length) {
                const remainingCount = unreadMails.length - detailed.length;
                notifyClient({
                  type: 'email',
                  title: 'More Unread Emails',
                  message: `${remainingCount} more unread email${remainingCount > 1 ? 's' : ''} in ${mailbox.EMAIL || 'your mailbox'}.`,
                  toastText: `${remainingCount} more unread email${remainingCount > 1 ? 's' : ''} detected.`,
                  toastVariant: 'info',
                  browserTitle: '📧 Unread Emails',
                  link: constant.Authedcomponent.mailBox.url,
                  originName: mailbox.EMAIL || '',
                  meta: {
                    mailboxId: mailbox.ID,
                    mailboxEmail: mailbox.EMAIL,
                    count: remainingCount,
                  },
                  dedupeKey: `email-bootstrap-summary-${mailboxId}-${unreadIds.slice(0, 5).join('|')}`,
                });
              }
            }

            mailboxSnapshotRef.current[mailboxId] = unreadIds;
            continue;
          }

          const previousUnreadSet = new Set(existingSnapshot);
          const newlyUnread = unreadMails.filter((mail) => !previousUnreadSet.has(String(mail.messageId)));

          if (newlyUnread.length > 0) {
            const detailed = newlyUnread.slice(0, 3);
            detailed.forEach((mail) => {
              const sender = mail.fromName || mail.fromAddress || 'Unknown sender';
              const subject = mail.subject || '(No subject)';

              notifyClient({
                type: 'email',
                title: 'New Email',
                message: `${sender}: ${subject}`,
                toastText: `New email from ${sender}`,
                toastVariant: 'info',
                browserTitle: '📧 New Email',
                link: buildMailboxNotificationLink({
                  messageId: mail.messageId,
                  mailboxId: mailbox.ID,
                  folder: 'INBOX',
                }),
                originName: sender,
                meta: {
                  mailboxId: mailbox.ID,
                  mailboxEmail: mailbox.EMAIL,
                  messageId: mail.messageId,
                  sender,
                  subject,
                },
                dedupeKey: `email-${mailboxId}-${mail.messageId}`,
              });
            });

            if (newlyUnread.length > detailed.length) {
              const remainingCount = newlyUnread.length - detailed.length;
              notifyClient({
                type: 'email',
                title: 'More Unread Emails',
                message: `${remainingCount} more new email${remainingCount > 1 ? 's' : ''} in ${mailbox.EMAIL || 'your mailbox'}.`,
                toastText: `${remainingCount} more unread email${remainingCount > 1 ? 's' : ''} received.`,
                toastVariant: 'info',
                browserTitle: '📧 New Emails',
                link: constant.Authedcomponent.mailBox.url,
                originName: mailbox.EMAIL || '',
                meta: {
                  mailboxId: mailbox.ID,
                  mailboxEmail: mailbox.EMAIL,
                  count: remainingCount,
                },
                dedupeKey: `email-summary-${mailboxId}-${unreadIds.slice(0, 5).join('|')}`,
              });
            }
          }

          mailboxSnapshotRef.current[mailboxId] = unreadIds;
        }

        setStorage(snapshotStoreKey, mailboxSnapshotRef.current);
      } catch (error) {
        console.error('Mailbox polling failed:', error);
      } finally {
        mailboxPollInFlightRef.current = false;
      }
    };

    const pollIntervalMs = getNetworkAwarePollingIntervalMs('mailbox');
    pollMailboxForNewUnread();
    const interval = setInterval(pollMailboxForNewUnread, pollIntervalMs);

    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [buildMailboxNotificationLink, notifyClient, sessionToken]);

  return (
    <>
      {canUseChat && <FloatingChatDock />}
      {sessionToken && <AIAssistantButton />}
      <ToastContainer />
      <Router onlineUsers={onlineUsers} />
    </>
  );
}

export default AppWrapper;
